import { z } from "zod";
import {
  medicalCompetences,
  medicalCompetenceSchema,
  type IEmployeeCompetence,
  type IMedicalCompetence,
  type IScheduledShift,
} from "./shifts";

const activityRequirements = [...medicalCompetences, "DoubleStaffing"] as const;
export const activityRequirementSchema = z.enum(activityRequirements);
export type IActivityRequirement = z.infer<typeof activityRequirementSchema>;

/**
 * Defines the competence fulfillment hierarchy.
 *
 * This hierarchy determines which competences can fulfill requirements for other competences
 * when assigning staff to activities.
 *
 * Doctor hierarchy:
 * - MedicalDoctor can fulfill AdvancedNursePractitioner and DoctorAssistant requirements
 * - AdvancedNursePractitioner is treated as "junior doctor" work in this context
 * - DoctorAssistant is treated as "junior doctor" work similar to ANP
 * - ANP and DA cannot fulfill each other's requirements (separate roles)
 *
 * Nursing hierarchy:
 * - AdvancedNursePractitioner can fulfill NursePractitioner and NurseAssistant requirements
 * - DoctorAssistant can fulfill NursePractitioner and NurseAssistant requirements
 * - NursePractitioner can fulfill NurseAssistant requirements
 *
 * Note: Support can be fulfilled by anyone with any competence (handled separately)
 */
const FULFILLMENT_HIERARCHY: Partial<
  Record<IMedicalCompetence, IMedicalCompetence[]>
> = {
  MedicalDoctor: [
    medicalCompetenceSchema.enum.AdvancedNursePractitioner,
    medicalCompetenceSchema.enum.DoctorAssistant,
  ],
  AdvancedNursePractitioner: [
    medicalCompetenceSchema.enum.NursePractitioner,
    medicalCompetenceSchema.enum.NurseAssistant,
  ],
  DoctorAssistant: [
    medicalCompetenceSchema.enum.NursePractitioner,
    medicalCompetenceSchema.enum.NurseAssistant,
  ],
  NursePractitioner: [medicalCompetenceSchema.enum.NurseAssistant],
};

const canFulfill = (
  assignedCompetence: IMedicalCompetence,
  requirement: IMedicalCompetence,
): boolean => {
  // Support can be fulfilled by anyone
  if (requirement === "Support") {
    return true;
  }

  // Exact match
  if (assignedCompetence === requirement) {
    return true;
  }

  // Check hierarchy
  const canAlsoFulfill = FULFILLMENT_HIERARCHY[assignedCompetence];
  return canAlsoFulfill?.includes(requirement) ?? false;
};

/**
 * Seeds the required competences of a new activity from the competence of the employee
 * creating it. An employee without a medical competence seeds no requirement at all.
 */
export const defaultRequiredCompetences = (
  employeeCompetence: IEmployeeCompetence,
): IMedicalCompetence[] =>
  employeeCompetence === null ? [] : [employeeCompetence];

/**
 * Reduces a list of required competences to its canonical form: de-duplicated and
 * with hierarchy-redundant competences removed for display purposes.
 *
 * A requirement is redundant when another required competence sits above it in the
 * fulfillment hierarchy — every assignee able to satisfy the survivor can also satisfy
 * the removed one (the same relation `canFulfill` encodes above).
 *
 * Key principle: An AdvancedNursePractitioner requirement is interpreted as junior doctor work,
 * not as advanced nursing work. DoctorAssistant follows the same principle.
 * This means:
 * - [MD, ANP] → [MD] (doctor work only, MD covers it)
 * - [MD, DA] → [MD] (doctor work only, MD covers it)
 * - [MD, ANP, NP] → [MD, NP] (doctor work + nursing work, both needed)
 * - [ANP, NP, NA] → [ANP, NP] (ANP as junior doctor + nursing work below that level)
 * - [DA, NP, NA] → [DA, NP] (DA as junior doctor + nursing work below that level)
 *
 * Conservative simplification rules to prevent ambiguity:
 * 1. Support is removed if other competences are present
 * 2. ANP is removed if MD is present (ANP represents junior doctor work)
 * 3. DA is removed if MD is present (DA represents junior doctor work)
 * 4. NA is removed if NP is present (within nursing hierarchy)
 * 5. NP is NEVER removed when ANP or DA is present (prevents MD from fulfilling pure nursing work)
 */
export const getSimplifiedRequiredCompetences = (
  competences: IMedicalCompetence[],
): IMedicalCompetence[] => {
  if (!competences || competences.length === 0) {
    return [];
  }

  let result = [...new Set(competences)];

  if (result.length === 1) {
    return result;
  }

  // Remove Support if there are other competences
  if (result.includes(medicalCompetenceSchema.enum.Support)) {
    result = result.filter((c) => c !== medicalCompetenceSchema.enum.Support);
  }

  // Remove ANP if MD is present (ANP alone = doctor work)
  if (
    result.includes(medicalCompetenceSchema.enum.MedicalDoctor) &&
    result.includes(medicalCompetenceSchema.enum.AdvancedNursePractitioner)
  ) {
    result = result.filter(
      (c) => c !== medicalCompetenceSchema.enum.AdvancedNursePractitioner,
    );
  }

  // Remove DA if MD is present (DA alone = doctor work)
  if (
    result.includes(medicalCompetenceSchema.enum.MedicalDoctor) &&
    result.includes(medicalCompetenceSchema.enum.DoctorAssistant)
  ) {
    result = result.filter(
      (c) => c !== medicalCompetenceSchema.enum.DoctorAssistant,
    );
  }

  // Simplify within nursing hierarchy: NA→NP
  if (
    result.includes(medicalCompetenceSchema.enum.NursePractitioner) &&
    result.includes(medicalCompetenceSchema.enum.NurseAssistant)
  ) {
    result = result.filter(
      (c) => c !== medicalCompetenceSchema.enum.NurseAssistant,
    );
  }

  return result;
};

/**
 * Aggregates the required competences of several occurrences (e.g. all activities in a
 * group or visit) into a single canonical, simplified requirement set.
 *
 * Only needed where no aggregated field exists — notably visits, which are modelled
 * purely as a list of occurrences.
 */
export const aggregateRequiredCompetences = (
  occurrences: ReadonlyArray<{
    requiredCompetences: ReadonlyArray<IMedicalCompetence>;
  }>,
): IMedicalCompetence[] =>
  getSimplifiedRequiredCompetences(
    occurrences.flatMap((occurrence) => [...occurrence.requiredCompetences]),
  );

/**
 * Determines which requirements are not fulfilled by the assigned staff.
 *
 * This function checks that doctors do work according to the doctor hierarchy,
 * nurses according to the nursing hierarchy, and other competences do their own work.
 *
 * Special cases:
 * - Support can be fulfilled by anyone with any competence
 * - DoubleStaffing requires at least 2 assignees regardless of competence
 */
export const getUnfulfilledRequirements = ({
  assignees,
  requiredCompetences,
  doubleStaffing = false,
}: {
  assignees: ReadonlyArray<Pick<IScheduledShift, "competence">>;
  requiredCompetences: ReadonlyArray<IMedicalCompetence>;
  doubleStaffing?: boolean;
}): IActivityRequirement[] => {
  const requirements: IActivityRequirement[] = [
    ...requiredCompetences,
    ...(doubleStaffing ? [activityRequirementSchema.enum.DoubleStaffing] : []),
  ];

  const assignedShiftsCompetences = assignees.map(
    ({ competence }) => competence,
  );

  return requirements.filter((requirement) => {
    if (requirement === activityRequirementSchema.enum.Support) {
      return assignees.length === 0;
    }
    if (requirement === activityRequirementSchema.enum.DoubleStaffing) {
      return assignees.length <= 1;
    }
    return !assignedShiftsCompetences.some((competence) =>
      canFulfill(competence, requirement),
    );
  });
};
